/* eslint-disable react/no-multi-comp, import/no-extraneous-dependencies */
import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import {
  Search,
  Paperclip,
  Send,
  Check,
  CheckCheck,
  Loader2,
  ArrowLeft,
  ChevronUp,
  ChevronDown,
  X,
} from 'lucide-react';
import axios from 'axios';
import { FaWhatsapp } from 'react-icons/fa6';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { authContext } from 'contexts/AuthContext';
import './Chat.scss';

const REST_API = process.env.REACT_APP_REST_API_ENDPOINT;
const WHATSAPP_SEND_API = REST_API?.replace(/\/api\/?$/, '');
const WS_BASE = REST_API?.replace(/\/api\/?$/, '');
const CHAT_REFRESH_INTERVAL = 5000;

/* ─── File type helper ──────────────────────────────────────────── */
const getFileType = (file) => {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('audio/')) return 'audio';
  if (file.type.startsWith('video/')) return 'video';
  return 'document';
};

/* ─── Helpers ─────────────────────────────────────────────────────── */

const formatTime = (val) => {
  // Supports both array [y,m,d,h,min,...] and ISO string "2026-08-06T13:38:00"
  if (!val) return '';
  if (Array.isArray(val)) {
    const [, , , hour, min] = val;
    return `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
  }
  if (typeof val === 'string') {
    const d = new Date(val);
    if (!Number.isNaN(d.getTime())) {
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }
  }
  return '';
};

// WhatsApp-style date/time formatter for contact list
const formatContactTime = (val) => {
  if (!val) return '';

  let date;
  if (Array.isArray(val)) {
    const [year, month, day, hour = 0, min = 0, sec = 0, nano = 0] = val;
    date = new Date(year, month - 1, day, hour, min, sec, Math.floor(nano / 1000000));
  } else if (typeof val === 'string') {
    date = new Date(val);
  } else if (typeof val === 'number') {
    date = new Date(val);
  }

  if (!date || Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // Today: show time only (10:30)
  if (msgDate.getTime() === today.getTime()) {
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }

  // Yesterday: show "Yesterday"
  if (msgDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  }

  // Older: show date (13 Aug)
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${date.getDate()} ${months[date.getMonth()]}`;
};

// Format date separator for chat messages (4/8/2026)
const formatDateSeparator = (val) => {
  if (!val) return '';

  let date;
  if (Array.isArray(val)) {
    const [year, month, day, hour = 0, min = 0, sec = 0, nano = 0] = val;
    date = new Date(year, month - 1, day, hour, min, sec, Math.floor(nano / 1000000));
  } else if (typeof val === 'string') {
    date = new Date(val);
  } else if (typeof val === 'number') {
    date = new Date(val);
  }

  if (!date || Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // Today
  if (msgDate.getTime() === today.getTime()) {
    return 'Today';
  }

  // Yesterday
  if (msgDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  }

  // Older: D/M/YYYY format
  return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
};

const getTimestamp = (val) => {
  if (!val) return 0;
  if (Array.isArray(val)) {
    const [year, month, day, hour = 0, min = 0, sec = 0, nano = 0] = val;
    return new Date(year, month - 1, day, hour, min, sec, Math.floor(nano / 1000000)).getTime();
  }
  const timestamp = typeof val === 'number' ? val : new Date(val).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const isSameMessageList = (current, next) => (
  current.length === next.length
  && current.every((message, index) => {
    const updated = next[index];
    return message.id === updated.id
      && message.text === updated.text
      && message.messageType === updated.messageType
      && message.status === updated.status
      && message.fullDate === updated.fullDate;
  })
);

const getAvatarLetters = (name) => {
  if (!name) return '??';
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const formatPhone = (phone) => {
  if (!phone) return '';
  const str = String(phone);
  if (str.startsWith('91') && str.length === 12)
    return `+91-${str.slice(2)}`;
  return `+${str}`;
};

const getContactDisplayName = (profileName, phone) => {
  const name = String(profileName || '').trim();
  const formattedPhone = formatPhone(phone);
  if (name && formattedPhone) return `${name} (${formattedPhone})`;
  return name || formattedPhone;
};

// Chat list and message endpoints can expose the WhatsApp contact name under
// different DTO fields. Normalize them so the sidebar can show it immediately.
const getProfileName = (item) => (
  item?.profileName
  || item?.pushName
  || item?.contactName
  || item?.name
  || item?.whatsappProfileName
  || ''
).toString().trim();

const sortContactsByLatestMessage = (items) => (
  [...items].sort((a, b) => (b.latestMessageTimestamp || 0) - (a.latestMessageTimestamp || 0))
);

const AVATAR_COLORS = [
  '#25D366', '#128C7E', '#075E54', '#34B7F1',
  '#00BCD4', '#9C27B0', '#FF5722', '#607D8B',
  '#E91E63', '#3F51B5', '#FF9800', '#4CAF50',
];

const getAvatarColor = (phone) => {
  let hash = 0;
  const str = String(phone);
  for (let i = 0; i < str.length; i += 1)
    hash = str.charCodeAt(i) + (Math.imul(hash, 32) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getMessagePreview = (msg) => {
  if (msg.messageType === 'audio') return '🎵 Audio';
  if (msg.messageType === 'image') return '📷 Image';
  if (msg.messageType === 'video') return '📹 Video';
  if (msg.messageType === 'document') return '📄 Document';
  if (msg.messageType === 'reaction') return '👍 Reaction';
  return msg.lastMessage || msg.messageBody || msg.text || '...';
};

const mapContactItem = (item) => ({
  id: item.phone,
  phone: item.phone,
  profileName: getProfileName(item),
  lastMessage: item.lastMessage || '',
  messageType: item.messageType || 'text',
  time: formatContactTime(item.lastMessageAt || item.receivedAt || item.createdAt),
  displayName: getContactDisplayName(getProfileName(item), item.phone),
  formattedPhone: formatPhone(item.phone),
  latestMessageTimestamp: getTimestamp(item.lastMessageAt || item.receivedAt || item.createdAt),
  unreadCount: Number(item.unreadCount || 0),
});

// Map a WebSocket DTO → contact list shape
const wsPayloadToContact = (dto) => {
  const phone = dto.phone || dto.senderNumber || dto.receiverNumber;
  const profileName = getProfileName(dto);
  return {
    id: phone,
    phone,
    profileName,
    lastMessage: dto.messageBody || dto.text || '',
    messageType: dto.messageType || 'text',
    time: formatContactTime(dto.receivedAt || dto.createdAt),
    displayName: getContactDisplayName(profileName, phone),
    formattedPhone: formatPhone(phone),
    latestMessageTimestamp: getTimestamp(dto.receivedAt || dto.createdAt),
    unreadCount: 0,
  };
};

// Map a WebSocket DTO → message shape
const wsPayloadToMessage = (dto) => {
  const phone = dto.phone || dto.senderNumber || dto.receiverNumber;
  return {
    id: dto.id || `ws-${Date.now()}`,
    text: dto.messageBody || dto.text || '',
    messageType: dto.messageType || 'text',
    sender: dto.direction === 'POLYSTOX' ? 'me' : 'them',
    direction: dto.direction,
    status: dto.messageStatus?.toLowerCase() || 'received',
    time: formatTime(dto.receivedAt || dto.createdAt),
    fullDate: dto.receivedAt || dto.createdAt,
    phone,
    profileName: dto.profileName,
    replyPreview: dto.replyPreview || '',
  };
};

/* ─── HighlightText ─────────────────────────────────────────────────── */
const HighlightText = ({ text, term }) => {
  if (!term || !term.trim() || !text) return text;
  const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) => {
        if (regex.test(part)) {
          // eslint-disable-next-line react/no-array-index-key
          return <mark key={i} className="search-highlight">{part}</mark>;
        }
        // eslint-disable-next-line react/no-array-index-key
        return <span key={i}>{part}</span>;
      })}
    </>
  );
};

const MessageStatusIcon = ({ status }) => {
  if (status === 'sending') return <Loader2 size={12} className="spin-icon" />;
  if (status === 'read') return <CheckCheck size={14} color="#6ec7ff" />;
  if (status === 'delivered') return <CheckCheck size={14} color="#858B8F" />;
  return <Check size={14} color="#858B8F" />;
};

const AttachmentContent = ({ msg }) => {
  const attachmentUrl = msg.text;
  const label = msg.messageType === 'image' ? 'Image' : 'Document';

  if (!attachmentUrl) return <span className="message-attachment-fallback">{label}</span>;

  if (msg.messageType === 'image') {
    return (
      <a
        className="message-image-link"
        href={attachmentUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open image"
      >
        <img
          className="message-image"
          src={attachmentUrl}
          alt="WhatsApp attachment"
          loading="lazy"
        />
      </a>
    );
  }

  return (
    <a
      className="message-document-link"
      href={attachmentUrl}
      target="_blank"
      rel="noopener noreferrer"
      download
    >
      <span className="message-document-icon" aria-hidden="true">📄</span>
      <span>{label}</span>
    </a>
  );
};

const MessageBubble = ({ msg, highlightTerm, isActiveMatch }) => {
  const isSent = msg.sender === 'me';
  const rowClass = `message-row ${isSent ? 'sent' : 'received'}${isActiveMatch ? ' active-search-match' : ''}`;
  const bubbleClass = `message-bubble ${isSent ? 'sent-bubble' : 'received-bubble'} ${msg.status === 'sending' ? 'sending' : ''}`;

  const isMedia = msg.messageType === 'image' || msg.messageType === 'video' || msg.messageType === 'audio';
  const isDocument = msg.messageType === 'document';
  const isAttachment = isMedia || isDocument;
  const replyPreview = typeof msg.replyPreview === 'string' ? msg.replyPreview.trim() : '';
  const replyPreviewBubble = replyPreview ? (
    <div className="message-row sent reply-preview-row">
      <div className="message-bubble sent-bubble reply-preview-bubble">
        <span className="reply-preview-sender">Polystox</span>
        <span className="message-text">{replyPreview}</span>
      </div>
    </div>
  ) : null;

  /* ── Media (image / video / audio): no bubble background ── */
  if (isMedia && msg.text && msg.text.startsWith('http')) {
    return (
      <>
        {replyPreviewBubble}
        <div id={`chat-msg-${msg.id}`} className={rowClass}>
          <div className={`message-bubble message-bubble--media ${msg.status === 'sending' ? 'sending' : ''}`}>
            {msg.messageType === 'image' && (
              <a className="message-image-link" href={msg.text} target="_blank" rel="noopener noreferrer" aria-label="Open image">
                <img className="message-image" src={msg.text} alt="Shared media" loading="lazy" />
              </a>
            )}
            {msg.messageType === 'video' && (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video src={msg.text} controls className="message-video" style={{ width: 'min(280px, 60vw)', maxHeight: '320px', borderRadius: '8px', display: 'block' }} />
            )}
            {msg.messageType === 'audio' && (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <audio controls src={msg.text} style={{ maxWidth: '240px', height: '40px' }} />
            )}
            <div className="message-meta message-meta--overlay">
              <span className="message-time" style={{ color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{msg.time}</span>
              {isSent && <span className="message-status"><MessageStatusIcon status={msg.status} /></span>}
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ── Document or text ── */
  return (
    <>
      {replyPreviewBubble}
      <div id={`chat-msg-${msg.id}`} className={rowClass}>
        <div className={bubbleClass}>
          {isAttachment ? <AttachmentContent msg={msg} /> : (
            <span className="message-text"><HighlightText text={msg.text} term={highlightTerm} /></span>
          )}
          <div className="message-meta">
            <span className="message-time">{msg.time}</span>
            {isSent && <span className="message-status"><MessageStatusIcon status={msg.status} /></span>}
          </div>
        </div>
      </div>
    </>
  );
};

const MessagesContent = ({
  loadingMessages,
  messagesError,
  messages,
  onRetry,
  highlightTerm,
  activeMatchId,
}) => {
  if (loadingMessages) {
    return (
      <div className="chat-messages-loading">
        <Loader2 size={28} className="spin-icon" />
        <span>Loading messages...</span>
      </div>
    );
  }
  if (messagesError) {
    return (
      <div className="chat-messages-error">
        <span>{messagesError}</span>
        <button type="button" onClick={onRetry}>Retry</button>
      </div>
    );
  }
  if (messages.length === 0) {
    return (
      <div className="chat-no-messages">
        <p>No messages yet. Start a conversation!</p>
      </div>
    );
  }

  // Group messages by date for separators
  const messagesWithSeparators = [];
  let lastDateLabel = null;

  messages.forEach((msg, idx) => {
    const dateLabel = formatDateSeparator(msg.fullDate || msg.receivedAt);

    // Add date separator if date changed
    if (dateLabel && dateLabel !== lastDateLabel) {
      messagesWithSeparators.push({
        type: 'date-separator',
        id: `date-${idx}`,
        label: dateLabel,
      });
      lastDateLabel = dateLabel;
    }

    messagesWithSeparators.push({ type: 'message', data: msg });
  });

  return messagesWithSeparators.map((item) => {
    if (item.type === 'date-separator') {
      return (
        <div key={item.id} className="date-separator">
          <span className="date-separator-label">{item.label}</span>
        </div>
      );
    }
    return (
      <MessageBubble
        key={item.data.id}
        msg={item.data}
        highlightTerm={highlightTerm}
        isActiveMatch={activeMatchId && String(item.data.id) === String(activeMatchId)}
      />
    );
  });
};

const ContactListContent = ({
  loadingContacts,
  contactsError,
  contacts,
  searchQuery,
  isFetchingMoreContacts,
  activeContact,
  onRetry,
  onContactClick,
}) => {
  if (loadingContacts) {
    return (
      <div className="chat-list-loading">
        <Loader2 size={24} className="spin-icon" />
        <span>Loading chats...</span>
      </div>
    );
  }
  if (contactsError) {
    return (
      <div className="chat-list-error">
        <span>{contactsError}</span>
        <button type="button" onClick={onRetry}>Retry</button>
      </div>
    );
  }
  if (contacts.length === 0) {
    const emptyText = searchQuery ? `No results for "${searchQuery}"` : 'No chats found';
    return <div className="chat-list-empty">{emptyText}</div>;
  }
  return (
    <>
      {contacts.map((contact) => {
        const color = getAvatarColor(contact.phone);
        const letters = getAvatarLetters(contact.profileName || contact.phone);
        const isActive = activeContact?.id === contact.id;
        const hasUnread = contact.unreadCount > 0;

        return (
          <div
            key={contact.id}
            className={`chat-list-item ${isActive ? 'active' : ''} ${hasUnread ? 'has-unread' : ''}`}
            onClick={() => onContactClick(contact)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onContactClick(contact);
            }}
            role="button"
            tabIndex={0}
          >
            <div className="avatar-container">
              <div
                className="avatar-initials"
                style={{ backgroundColor: color }}
                aria-label={contact.profileName || contact.phone}
              >
                {letters}
              </div>
            </div>

            <div className="chat-item-details">
              <div className="chat-item-header">
                <span className={`chat-name ${hasUnread ? 'chat-name--unread' : ''}`} title={contact.displayName}>
                  {contact.displayName}
                </span>
                <span className="chat-time">{contact.time}</span>
              </div>
              <div className="chat-item-footer">
                <span className="chat-last-message" title={getMessagePreview(contact)}>
                  {getMessagePreview(contact)}
                </span>
                {hasUnread && (
                  <span className="unread-badge">{contact.unreadCount}</span>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {isFetchingMoreContacts && (
        <div className="chat-list-loading-more">
          <Loader2 size={16} className="spin-icon" />
          <span>Loading more...</span>
        </div>
      )}
    </>
  );
};

/* ─── Main Chat Component ──────────────────────────────────────────── */

const Chat = () => {
  const { auth } = useContext(authContext);
  const token = auth?.data?.token;

  /* ── Mobile view state ('list' | 'chat') ── */
  const [mobileView, setMobileView] = useState('list');

  /* ── Contact list ── */
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactsError, setContactsError] = useState(null);
  const [contactPage, setContactPage] = useState(0);
  const [contactTotalPages, setContactTotalPages] = useState(1);
  const [isFetchingMoreContacts, setIsFetchingMoreContacts] = useState(false);

  /* ── Search ── */
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const searchDebounceRef = useRef(null);

  /* ── Active contact + messages ── */
  const [activeContact, setActiveContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  const [msgPage, setMsgPage] = useState(0);
  const [msgTotalPages, setMsgTotalPages] = useState(1);
  const [isFetchingMoreMsgs, setIsFetchingMoreMsgs] = useState(false);

  /* ── Send message ── */
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const fileInputRef = useRef(null);

  /* ── WebSocket ── */
  const [wsConnected, setWsConnected] = useState(false);
  const stompClientRef = useRef(null);
  const chatSubRef = useRef(null);   // per-contact subscription
  const activeContactRef = useRef(null);   // always up-to-date ref for WS callbacks

  const messagesEndRef = useRef(null);
  const preserveScrollRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const isFetchingOlderRef = useRef(null);
  const contactListRef = useRef(null);
  const chatWindowRef = useRef(null);
  const inputRef = useRef(null);
  const messageSearchInputRef = useRef(null);

  /* ── In-Chat Message Search ── */
  const [isMessageSearchOpen, setIsMessageSearchOpen] = useState(false);
  const [messageSearchTerm, setMessageSearchTerm] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

  // Client-side message search: compute matching message IDs
  const matchingMessageIds = useMemo(() => {
    if (!messageSearchTerm.trim()) return [];
    const term = messageSearchTerm.toLowerCase().trim();
    return messages
      .filter((m) => (m.text && typeof m.text === 'string' && m.text.toLowerCase().includes(term)) || (m.messageBody && typeof m.messageBody === 'string' && m.messageBody.toLowerCase().includes(term)))
      .map((m) => String(m.id));
  }, [messages, messageSearchTerm]);

  // Reset match index when search term changes
  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [messageSearchTerm]);

  // Auto-scroll to active match inside chatWindowRef only
  useEffect(() => {
    if (matchingMessageIds.length > 0 && chatWindowRef.current) {
      const matchId = matchingMessageIds[currentMatchIndex];
      const el = document.getElementById(`chat-msg-${matchId}`);
      if (el) {
        const container = chatWindowRef.current;
        const containerRect = container.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const relativeTop = elRect.top - containerRect.top + container.scrollTop;
        container.scrollTo({
          top: relativeTop - container.clientHeight / 2,
          behavior: 'smooth',
        });
      }
    }
  }, [currentMatchIndex, matchingMessageIds]);

  // Auto-focus search input when opened
  useEffect(() => {
    if (isMessageSearchOpen && messageSearchInputRef.current) {
      setTimeout(() => messageSearchInputRef.current?.focus(), 50);
    }
  }, [isMessageSearchOpen]);

  // Fetch additional matching messages from API when searching so older messages get loaded and highlighted
  useEffect(() => {
    if (!messageSearchTerm.trim() || !activeContact?.phone || !token) return undefined;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${REST_API}whatsapp/chats/${activeContact.phone}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { search: messageSearchTerm.trim(), page: 0, size: 50 },
        });
        const content = res?.data?.content || [];
        if (content.length > 0) {
          const mapped = content.map((item) => ({
            id: item.id,
            text: item.messageBody || '',
            messageType: item.messageType || 'text',
            sender: item.direction === 'POLYSTOX' ? 'me' : 'them',
            direction: item.direction,
            status: item.messageStatus?.toLowerCase() || 'sent',
            time: formatTime(item.receivedAt),
            fullDate: item.receivedAt,
            phone: item.phone,
            profileName: item.profileName,
            replyPreview: item.replyPreview || '',
          }));
          setMessages((prev) => {
            const existingIds = new Set(prev.map((m) => String(m.id)));
            const newMsgs = mapped.filter((m) => !existingIds.has(String(m.id)));
            if (newMsgs.length === 0) return prev;
            const combined = [...newMsgs, ...prev];
            return combined.sort((a, b) => getTimestamp(a.fullDate) - getTimestamp(b.fullDate));
          });
        }
      } catch (err) {
        console.error('Search fetch error:', err);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [messageSearchTerm, activeContact?.phone, token]);

  const handleNextMatch = () => {
    if (matchingMessageIds.length === 0) return;
    setCurrentMatchIndex((prev) => (prev + 1) % matchingMessageIds.length);
  };

  const handlePrevMatch = () => {
    if (matchingMessageIds.length === 0) return;
    setCurrentMatchIndex((prev) => (prev - 1 + matchingMessageIds.length) % matchingMessageIds.length);
  };

  const handleCloseSearch = () => {
    setIsMessageSearchOpen(false);
    setMessageSearchTerm('');
    setCurrentMatchIndex(0);
  };

  // Auto-focus input when contact changes or when finished sending
  useEffect(() => {
    if (activeContact && !isSending && inputRef.current) {
      inputRef.current.focus();
    }
  }, [activeContact, isSending]);

  // Keep activeContactRef in sync
  useEffect(() => {
    activeContactRef.current = activeContact;
  }, [activeContact]);

  /* ═══ WEBSOCKET SETUP ═══════════════════════════════════════════════ */
  useEffect(() => {
    if (!token) return undefined;

    const client = new Client({
      webSocketFactory: () => new SockJS(`${WS_BASE}/ws-endpoint`),
      reconnectDelay: 5000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,

      onConnect: () => {
        setWsConnected(true);

        // ── Subscribe to chat LIST topic (sidebar updates) ──
        client.subscribe('/topic/whatsapp/chats', (frame) => {
          try {
            const dto = JSON.parse(frame.body);
            const senderPhone = dto.phone || dto.senderNumber || dto.receiverNumber;

            if (!senderPhone) {
              console.warn('WS payload missing phone number:', dto);
              return;
            }

            const isOutgoing = dto.direction === 'POLYSTOX';
            if (isOutgoing) {
              return; // Ignore outgoing messages to prevent routing to Polystox's own number
            }

            // Update contacts list: move/add to top, update last message
            setContacts((prev) => {
              const existing = prev.find((c) => c.id === senderPhone);
              const updated = wsPayloadToContact(dto);

              if (existing) {
                // Keep unread count: increment if not currently open AND not outgoing
                const isOpen = activeContactRef.current?.id === senderPhone;
                const shouldIncrement = !isOpen && !isOutgoing;
                const newCount = shouldIncrement ? (existing.unreadCount || 0) + 1 : (existing.unreadCount || 0);

                const merged = { ...existing, ...updated, unreadCount: newCount };
                // Remove from current position, insert at top
                return [merged, ...prev.filter((c) => c.id !== senderPhone)];
              }
              // Brand-new chat — add to top
              const isOpen = activeContactRef.current?.id === senderPhone;
              const unreadCount = (!isOpen && !isOutgoing) ? 1 : 0;
              return [{ ...updated, unreadCount }, ...prev];
            });

            // ── Append to open thread if it's the active contact ──
            if (activeContactRef.current?.id === senderPhone) {
              const newMsg = wsPayloadToMessage(dto);
              setMessages((prev) => {
                // De-duplicate by id
                if (prev.some((m) => String(m.id) === String(newMsg.id))) return prev;
                return [...prev, newMsg];
              });
            }
          } catch (err) {
            console.error('WS parse error (chats):', err);
          }
        });

        stompClientRef.current = client;
      },

      onDisconnect: () => setWsConnected(false),
      onStompError: (frame) => {
        console.error('STOMP error:', frame);
        setWsConnected(false);
      },
    });

    client.activate();
    stompClientRef.current = client;

    return () => {
      client.deactivate();
      stompClientRef.current = null;
      setWsConnected(false);
    };
  }, [token]);

  /* ═══ PER-CONTACT SUBSCRIPTION (open thread) ════════════════════════ */
  useEffect(() => {
    const client = stompClientRef.current;

    // Unsubscribe previous contact's topic
    if (chatSubRef.current) {
      try { chatSubRef.current.unsubscribe(); } catch (_) { /* ignore */ }
      chatSubRef.current = null;
    }

    if (!client || !client.connected || !activeContact?.phone) return;

    const topic = `/topic/whatsapp/chat/${activeContact.phone}`;
    chatSubRef.current = client.subscribe(topic, (frame) => {
      try {
        const dto = JSON.parse(frame.body);
        const newMsg = wsPayloadToMessage(dto);
        setMessages((prev) => {
          if (prev.some((m) => String(m.id) === String(newMsg.id))) return prev;

          const isOutgoing = dto.direction === 'POLYSTOX';
          if (isOutgoing) return prev; // Ignored to prevent duplicates or wrong ID matching

          return [...prev, newMsg];
        });
      } catch (err) {
        console.error('WS parse error (chat thread):', err);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeContact?.phone, wsConnected]);

  /* ═══ FETCH CONTACT LIST ════════════════════════════════════════════ */
  const fetchContacts = useCallback(
    async (page = 0, append = false, search = '', silent = false) => {
      if (!token) return;
      if (!silent) {
        if (page === 0 && !append) setLoadingContacts(true);
        else setIsFetchingMoreContacts(true);
        setContactsError(null);
      }

      const params = { page, size: 20 };
      if (search && search.trim()) params.search = search.trim();

      try {
        const res = await axios.get(`${REST_API}whatsapp/chats`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });
        const { data } = res;
        const content = data?.content || [];
        const mapped = content.map(mapContactItem);

        setContacts((prev) => {
          // Keep a known WhatsApp profile name when a refresh response omits it.
          // The list endpoint can return an empty profileName intermittently.
          const existingByPhone = new Map(prev.map((contact) => [contact.phone, contact]));
          const merged = mapped.map((contact) => {
            const existing = existingByPhone.get(contact.phone);
            if (!existing?.profileName || contact.profileName) return contact;

            return {
              ...contact,
              profileName: existing.profileName,
              displayName: getContactDisplayName(existing.profileName, contact.phone),
            };
          });
          return sortContactsByLatestMessage(append ? [...prev, ...merged] : merged);
        });
        setContactTotalPages(data?.totalPages || 1);
        setContactPage(page);

      } catch (err) {
        console.error('Error fetching contacts:', err);
        if (!silent) setContactsError('Failed to load chats. Please retry.');
      } finally {
        if (!silent) {
          setLoadingContacts(false);
          setIsSearching(false);
          setIsFetchingMoreContacts(false);
        }
      }
    },
    [token],
  );

  useEffect(() => {
    fetchContacts(0, false, '');
  }, [fetchContacts]);

  // Keep the chat list current even when a WebSocket event is missed.
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchContacts(0, false, searchQuery, true);
    }, CHAT_REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [fetchContacts, searchQuery]);

  /* ── Debounced search ── */
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    setIsSearching(true);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchContacts(0, false, val);
    }, 400);
  };

  /* ── Infinite scroll: contacts ── */
  const handleContactListScroll = () => {
    if (!contactListRef.current || isFetchingMoreContacts || contactPage + 1 >= contactTotalPages) return;
    const { scrollTop, scrollHeight, clientHeight } = contactListRef.current;
    if (scrollHeight - scrollTop - clientHeight < 80)
      fetchContacts(contactPage + 1, true, searchQuery);
  };

  /* ═══ FETCH MESSAGES ════════════════════════════════════════════════ */
  const fetchMessages = useCallback(
    async (phone, page = 0, prepend = false, silent = false) => {
      if (!token || !phone) return;
      if (prepend && isFetchingOlderRef.current === phone) return;
      if (prepend) isFetchingOlderRef.current = phone;
      if (!silent) {
        if (page === 0) setLoadingMessages(true);
        else setIsFetchingMoreMsgs(true);
        setMessagesError(null);
      }

      try {
        const res = await axios.get(`${REST_API}whatsapp/chats/${phone}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { page, size: 20 },
        });
        const { data } = res;
        const content = data?.content || [];

        // Ignore a delayed response if the user selected another conversation.
        if (activeContactRef.current?.phone !== phone) return;

        // The API returns newest first. Render oldest first so the newest message
        // stays at the bottom, matching WhatsApp's conversation order.
        const mapped = content.map((item) => ({
          id: item.id,
          text: item.messageBody || '',
          messageType: item.messageType || 'text',
          sender: item.direction === 'POLYSTOX' ? 'me' : 'them',
          direction: item.direction,
          status: item.messageStatus?.toLowerCase() || 'sent',
          time: formatTime(item.receivedAt),
          fullDate: item.receivedAt,
          phone: item.phone,
          profileName: item.profileName,
          replyPreview: item.replyPreview || '',
        })).reverse();

        // Capture the actual viewport immediately before inserting older rows.
        // Restoring in a layout effect avoids a paint at the bottom in between.
        if (prepend && chatWindowRef.current) {
          const container = chatWindowRef.current;
          const containerTop = container.getBoundingClientRect().top;
          const visibleMessage = [...container.querySelectorAll('.message-row[id]')].find(
            (element) => element.getBoundingClientRect().bottom > containerTop,
          );
          preserveScrollRef.current = {
            height: chatWindowRef.current.scrollHeight,
            top: chatWindowRef.current.scrollTop,
            anchorId: visibleMessage?.id,
            anchorTop: visibleMessage
              ? visibleMessage.getBoundingClientRect().top - containerTop
              : null,
          };
        }

        // The individual-chat API also carries the latest WhatsApp profile name.
        // Use it to repair older/sidebar entries where the list API returned no name.
        const responseProfileName = content.find(
            (item) => item.phone === phone && getProfileName(item),
        ) && getProfileName(content.find(
          (item) => item.phone === phone && getProfileName(item),
        ));
        if (responseProfileName) {
          setContacts((prev) => prev.map((contact) => (
            contact.id === phone
              ? {
                ...contact,
                profileName: responseProfileName,
                displayName: getContactDisplayName(responseProfileName, phone),
              }
              : contact
          )));
          setActiveContact((prev) => (prev?.phone === phone
            ? {
              ...prev,
              profileName: responseProfileName,
              displayName: getContactDisplayName(responseProfileName, phone),
            }
            : prev));
        }

        setMessages((prev) => {
          if (prepend) {
            const existingIds = new Set(prev.map((message) => String(message.id)));
            return [...mapped.filter((message) => !existingIds.has(String(message.id))), ...prev];
          }
          // Background refreshes fetch only page zero. Merge those latest rows
          // into the loaded history so polling never discards older pages.
          if (silent) {
            const mergedById = new Map(prev.map((message) => [String(message.id), message]));
            mapped.forEach((message) => {
              const id = String(message.id);
              mergedById.set(id, { ...mergedById.get(id), ...message });
            });
            const merged = [...mergedById.values()].sort(
              (a, b) => getTimestamp(a.fullDate) - getTimestamp(b.fullDate),
            );
            return isSameMessageList(prev, merged) ? prev : merged;
          }
          // Avoid re-rendering when the fetched page is unchanged.
          return isSameMessageList(prev, mapped) ? prev : mapped;
        });
        setMsgTotalPages(data?.totalPages || 1);
        // A page-zero poll must not rewind pagination after the user has loaded
        // older pages; that would make the same page load again on the next scroll.
        if (!silent) setMsgPage(page);
      } catch (err) {
        console.error('Error fetching messages:', err);
        if (!silent) setMessagesError('Failed to load messages.');
      } finally {
        if (!silent) {
          setLoadingMessages(false);
          if (!prepend) setIsFetchingMoreMsgs(false);
        }
        if (prepend && isFetchingOlderRef.current === phone) {
          isFetchingOlderRef.current = null;
          setIsFetchingMoreMsgs(false);
        }
      }
    },
    [token],
  );

  // Load messages + clear unread when switching contacts
  useEffect(() => {
    if (activeContact?.phone) {
      isNearBottomRef.current = true;
      preserveScrollRef.current = null;
      isFetchingOlderRef.current = null;
      setMessages([]);
      setIsFetchingMoreMsgs(false);
      setMsgPage(0);
      setMsgTotalPages(1);
      fetchMessages(activeContact.phone, 0, false);

      // Clear unread badge for this contact
      setContacts((prev) =>
        prev.map((c) => {
          if (c.id === activeContact.phone) return { ...c, unreadCount: 0 };
          return c;
        }),
      );
    }
  }, [activeContact?.phone, fetchMessages]);

  // Refresh the open conversation in the background for sent, received, and status updates.
  useEffect(() => {
    if (!activeContact?.phone) return undefined;

    const intervalId = setInterval(() => {
      fetchMessages(activeContact.phone, 0, false, true);
    }, CHAT_REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [activeContact?.phone, fetchMessages]);

  // Keep the same visible messages after prepending history. Follow new messages
  // only when the reader was already near the bottom of the conversation.
  useLayoutEffect(() => {
    const container = chatWindowRef.current;
    if (!container) return;

    if (preserveScrollRef.current) {
      const previous = preserveScrollRef.current;
      const anchor = previous.anchorId && document.getElementById(previous.anchorId);
      if (anchor) {
        const containerTop = container.getBoundingClientRect().top;
        container.scrollTop += anchor.getBoundingClientRect().top - containerTop - previous.anchorTop;
      } else {
        container.scrollTop = container.scrollHeight - previous.height + previous.top;
      }
      preserveScrollRef.current = null;
      return;
    }

    if (isNearBottomRef.current) container.scrollTop = container.scrollHeight;
  }, [messages]);

  /* ── Infinite scroll: chat window (scroll up = older) ── */
  const handleChatWindowScroll = () => {
    if (!chatWindowRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = chatWindowRef.current;
    isNearBottomRef.current = scrollHeight - scrollTop - clientHeight < 80;
    if (
      isFetchingMoreMsgs
      || isFetchingOlderRef.current === activeContact?.phone
      || msgPage + 1 >= msgTotalPages
    ) return;
    if (scrollTop < 80)
      fetchMessages(activeContact?.phone, msgPage + 1, true);
  };

  /* ── Contact click ── */
  const handleContactClick = (contact) => {
    setSendError(null);
    setActiveContact(contact);
    setIsMessageSearchOpen(false);
    setMessageSearchTerm('');
    setMobileView('chat'); // Switch to chat view on mobile

    if (contact.unreadCount > 0) {
      // Clear the badge immediately, then persist the seen status in the API.
      setContacts((prev) => prev.map((item) => (
        item.id === contact.id ? { ...item, unreadCount: 0 } : item
      )));

      // Use the REST endpoint configured in .env.
      axios.post(`${REST_API}whatsapp/chats/${contact.phone}/seen`)
        .then(() => {
          fetchContacts(0, false, searchQuery, true);
          window.dispatchEvent(new Event('whatsapp:unread-count-changed'));
        })
        .catch((err) => {
          console.error('Error marking WhatsApp chat as seen:', err);
        });
    }
  };

  /* ── Back to list (mobile) ── */
  const handleBackToList = () => {
    setMobileView('list');
  };

  /* ═══ SEND MESSAGE via API ══════════════════════════════════════════ */
  const handleSendMessage = async (e) => {
    e.preventDefault();
    const trimmed = messageText.trim();
    if (!trimmed || !activeContact || isSending) return;

    setIsSending(true);
    setSendError(null);

    const now = new Date();
    const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const tempId = `temp-${Date.now()}`;
    const tempMsg = {
      id: tempId, text: trimmed, messageType: 'text',
      sender: 'me', direction: 'POLYSTOX', status: 'sending', time: timeStr,
      fullDate: now,
    };

    isNearBottomRef.current = true;
    setMessages((prev) => [...prev, tempMsg]);
    setMessageText('');

    try {
      await axios.post(`${WHATSAPP_SEND_API}/send-message`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { phone: activeContact.phone, message: trimmed },
      });

      setMessages((prev) =>
        prev.map((m) => {
          if (m.id === tempId) return { ...m, status: 'sent' };
          return m;
        }),
      );

      // Move contact to top and update last message
      setContacts((prev) => {
        const existing = prev.find((c) => c.id === activeContact.phone);
        if (existing) {
          const updated = {
            ...existing,
            lastMessage: trimmed,
            time: formatContactTime(now),
            latestMessageTimestamp: Date.now(),
          };
          return [updated, ...prev.filter((c) => c.id !== activeContact.phone)];
        }
        return prev;
      });
    } catch (err) {
      console.error('Send error:', err);
      setSendError('Failed to send message. Please try again.');
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      setMessageText(trimmed);
    } finally {
      setIsSending(false);
    }
  };

  /* ═══ SEND ATTACHMENT ══════════════════════════════════════ */
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeContact) return;

    if (file.size > 16 * 1024 * 1024) {
      setSendError('File size should not exceed 16 MB.');
      e.target.value = '';
      return;
    }

    const fileType = getFileType(file);
    setIsUploadingFile(true);
    setSendError(null);

    const now = new Date();
    const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const tempId = `temp-${Date.now()}`;
    const tempMsg = {
      id: tempId,
      text: file.name,
      messageType: fileType,
      sender: 'me',
      direction: 'POLYSTOX',
      status: 'sending',
      time: timeStr,
      fullDate: now,
    };
    isNearBottomRef.current = true;
    setMessages((prev) => [...prev, tempMsg]);

    try {
      // Step 1: Upload file to get blob URL
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('contentType', file.type || 'application/octet-stream');
      formData.append('mimeType', file.type || 'application/octet-stream');
      const uploadRes = await axios.post(`${REST_API}common/upload-file`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const uploadedUrl = uploadRes?.data?.data;
      if (!uploadedUrl) throw new Error('Upload failed: no URL returned');

      // Step 2: Send the media message
      await axios.post(`${WHATSAPP_SEND_API}/send-message`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { phone: activeContact.phone, message: uploadedUrl, type: fileType },
      });

      setMessages((prev) =>
        prev.map((m) => (m.id === tempId ? { ...m, text: uploadedUrl, status: 'sent' } : m)),
      );

      // Update contact last message
      setContacts((prev) => {
        const existing = prev.find((c) => c.id === activeContact.phone);
        if (existing) {
          const updated = {
            ...existing,
            lastMessage: file.name,
            messageType: fileType,
            time: formatContactTime(now),
            latestMessageTimestamp: Date.now(),
          };
          return [updated, ...prev.filter((c) => c.id !== activeContact.phone)];
        }
        return prev;
      });
    } catch (err) {
      console.error('File send error:', err);
      setSendError('Failed to send file. Please try again.');
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
    } finally {
      setIsUploadingFile(false);
      e.target.value = '';
    }
  };

  /* ═══ RENDER ════════════════════════════════════════════════════════ */
  return (
    <div className={`chat-app-container mobile-view-${mobileView}`}>
      {/* ── Left Sidebar ── */}
      <div className="chat-sidebar mobile-panel">
        <div className="chat-sidebar-header">
          <div className="user-profile">
            <div className="avatar" style={{ background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaWhatsapp size={24} color="#25D366" />
            </div>
            <span className="title">
              WhatsApp Chat
            </span>
          </div>

        </div>

        <div className="chat-search-container">
          <div className="search-bar">
            {isSearching
              ? <Loader2 size={18} className="search-icon spin-icon" />
              : <Search size={18} className="search-icon" />
            }
            <input
              type="text"
              placeholder="Search by number or name..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
        </div>

        <div className="chat-list" ref={contactListRef} onScroll={handleContactListScroll}>
          <ContactListContent
            loadingContacts={loadingContacts}
            contactsError={contactsError}
            contacts={contacts}
            searchQuery={searchQuery}
            isFetchingMoreContacts={isFetchingMoreContacts}
            activeContact={activeContact}
            onRetry={() => fetchContacts(0, false, '')}
            onContactClick={handleContactClick}
          />
        </div>
      </div>

      {/* ── Right Chat Window ── */}
      <div className="chat-window mobile-panel">
        {activeContact ? (
          <>
            <div className="chat-window-header">
              <div className="chat-header-info">
                <button
                  type="button"
                  className="mobile-back-btn"
                  onClick={handleBackToList}
                  aria-label="Back to chats"
                >
                  <ArrowLeft size={22} />
                </button>
                <div
                  className="avatar-initials avatar-initials--header"
                  style={{ backgroundColor: getAvatarColor(activeContact.phone) }}
                >
                  {getAvatarLetters(activeContact.profileName || activeContact.phone)}
                </div>
                <div className="chat-header-text">
                  <span className="chat-header-name">{activeContact.displayName}</span>
                  <span className="chat-header-status">
                    {wsConnected ? (
                      <span className="ws-live-pill">● Live</span>
                    ) : (
                      <span className="ws-offline-pill">Reconnecting...</span>
                    )}
                  </span>
                </div>
              </div>
              <div className="chat-header-actions">
                {isMessageSearchOpen ? (
                  <div className="chat-window-header-search">
                    <div className="search-input-wrapper">
                      <Search size={15} className="search-inline-icon" />
                      <input
                        ref={messageSearchInputRef}
                        type="text"
                        className="message-search-input"
                        placeholder="Search in chat..."
                        value={messageSearchTerm}
                        onChange={(e) => setMessageSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (e.shiftKey) handlePrevMatch();
                            else handleNextMatch();
                          } else if (e.key === 'Escape') {
                            handleCloseSearch();
                          }
                        }}
                      />
                      {messageSearchTerm && (
                        <span className="search-match-count">
                          {matchingMessageIds.length > 0
                            ? `${currentMatchIndex + 1}/${matchingMessageIds.length}`
                            : 'No match'}
                        </span>
                      )}
                    </div>
                    {matchingMessageIds.length > 0 && (
                      <div className="search-nav-buttons">
                        <button
                          type="button"
                          className="search-nav-btn"
                          onClick={handlePrevMatch}
                          title="Previous (Shift+Enter)"
                          aria-label="Previous match"
                        >
                          <ChevronUp size={16} />
                        </button>
                        <button
                          type="button"
                          className="search-nav-btn"
                          onClick={handleNextMatch}
                          title="Next (Enter)"
                          aria-label="Next match"
                        >
                          <ChevronDown size={16} />
                        </button>
                      </div>
                    )}
                    <button
                      type="button"
                      className="search-close-btn"
                      onClick={handleCloseSearch}
                      aria-label="Close search"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="header-icon-btn"
                    onClick={() => setIsMessageSearchOpen(true)}
                    title="Search messages"
                    aria-label="Search messages"
                  >
                    <Search size={20} className="icon-btn" />
                  </button>
                )}

              </div>
            </div>

            <div className="chat-messages-container" ref={chatWindowRef} onScroll={handleChatWindowScroll}>
              {isFetchingMoreMsgs && (
                <div className="chat-loading-more-msgs">
                  <Loader2 size={16} className="spin-icon" />
                  <span>Loading older messages...</span>
                </div>
              )}
              <MessagesContent
                loadingMessages={loadingMessages}
                messagesError={messagesError}
                messages={messages}
                onRetry={() => fetchMessages(activeContact.phone, 0, false)}
                highlightTerm={messageSearchTerm}
                activeMatchId={matchingMessageIds[currentMatchIndex]}
              />
              <div ref={messagesEndRef} />
            </div>

            {sendError && <div className="chat-send-error">⚠️ {sendError}</div>}

            <div className="chat-input-container">
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
                id="chat-file-input"
              />
              <button
                type="button"
                className="icon-btn attach-icon"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingFile || isSending || !activeContact}
                title="Send attachment"
                aria-label="Attach file"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
              >
                {isUploadingFile
                  ? <Loader2 size={22} className="spin-icon" color="#8696a0" />
                  : <Paperclip size={22} color={activeContact ? '#54656f' : '#8696a0'} />}
              </button>
              <form onSubmit={handleSendMessage} className="chat-input-form">
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Type a message"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  className="chat-input"
                  disabled={isSending}
                />
                <button
                  type="submit"
                  className="send-btn"
                  disabled={!messageText.trim() || isSending}
                >
                  {isSending
                    ? <Loader2 size={20} className="spin-icon" color="#00a884" />
                    : <Send size={20} color={messageText.trim() ? '#00a884' : '#8696a0'} />
                  }
                </button>
              </form>
            </div>
          </>
        ) : (
          <div className="chat-placeholder">
            <h3>Polystox Chat</h3>
            <p>Select a contact to start messaging</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chat;
